<template>
  <article>
    <h1 class="text-h3" id="intro" v-intersect="onIntersect">Files</h1>
    <p>
      A file is an object on a computer that stores data and information. Files are the primary data source in Sky.
    </p>

    <p>
      In most cases, data looks like a table, as seen below. A 2-dimensional arrangement of rows and columns with data
      in corresponding intersections.
    </p>
    <v-card outlined color="grey lighten-3">
      <v-card-title>Completion Parameters</v-card-title>
      <v-card-subtitle>
        Data consisting of well completion details, often used to store various parameters on individual wells in
        each row.
      </v-card-subtitle>
      <v-data-table
        tile
        dense
        :items="completion.items"
        :headers="completion.headers"
        hide-default-footer
        disable-sort
      ></v-data-table>
    </v-card>
    <v-card outlined color="grey lighten-3">
      <v-card-title>Production Data</v-card-title>
      <v-card-subtitle>
        Data consisting of well production history, i.e. individual months of production for each well.
      </v-card-subtitle>
      <v-data-table
        dense
        :items="production.items"
        :headers="production.headers"
        hide-default-footer
        disable-sort
      ></v-data-table>
    </v-card>

    <h2 class="text-h4" id="fileCreate" v-intersect="onIntersect">Creating files</h2>
    <p>File creation allows users to...</p>
    <ul>
      <li>
        <router-link :to="{ path: '/help/files', hash: '#upload' }">upload</router-link>
        data from their machines, or
      </li>
      <li>
        <router-link :to="{ path: '/help/files', hash: '#import' }">import</router-link>
        data from a database of their choosing.
      </li>
    </ul>

    <v-alert text border="left" type="info">
      File creation requires permissions. Please contact your account manager for required permissions.
    </v-alert>

    <h3 class="text-h5" id="upload" v-intersect="onIntersect">Uploading</h3>
    <p>Users can upload any number of files.</p>
    <v-card flat outlined color="grey">
      <v-img contain eager src="@/assets/images/file_upload.png"></v-img>
    </v-card>
    <ol>
      <li>
        Navigate to
        <code>Files > Create</code>
        by clicking the
        <v-icon color="grey">mdi-file-document-outline</v-icon>
        button in the drawer, and then clicking
        <v-icon>mdi-plus</v-icon>
        button in the toolbar.
      </li>
      <li>
        Click the file input box where it says
        <strong>Choose a file</strong>
        to select a file from your system.
      </li>
      <li>
        Depending on how large a file is being uploaded, the next control will load a list of columns available in the
        file. Select the column that identifies the unique well identifier for this particular file.
      </li>
      <li>
        If the file is of an acceptable format and size, the
        <code class="green white--text">CREATE</code>
        button will be enabled. Click the button to upload the file.
      </li>
    </ol>

    <h4 class="text-h6" id="allowed" v-intersect="onIntersect">
      Allowed file types
    </h4>
    <p>
      Files can be uploaded in
      <strong class="info--text">CSV</strong>
      (Comma-separated Values), and
      <strong class="info--text">XLS / XLSX</strong>
      (Microsoft Excel) formats only.
    </p>

    <v-alert border="left" text type="info">
      Data from other plain-text formats such as
      <strong>JSON</strong>
      or
      <strong>XML</strong>
      is not supported at the moment.
    </v-alert>

    <h4 class="text-h6" id="limits" v-intersect="onIntersect">
      File size limits
    </h4>
    <p>
      File uploads are limited to
      <strong class="info--text">50MB</strong>
      per file.
    </p>

    <h3 class="text-h5" id="import" v-intersect="onIntersect">Importing</h3>

    <p>Users can import any amount of data.</p>

    <v-card flat outlined color="grey">
      <v-img contain eager src="@/assets/images/file_import.png"></v-img>
    </v-card>

    <ol>
      <li>
        Click
        <v-icon>mdi-file-document-outline</v-icon>
        in the drawer, click
        <v-icon>mdi-plus</v-icon>
        in the toolbar, and select the
        <code>IMPORT</code>
        tab.
      </li>
      <li>Select the database connection from which you want to import data</li>
      <li>Select the table and columns you want to import</li>
      <li>Select the unique well identifier</li>
      <li>
        Click
        <code class="green white--text">CREATE</code>
        to import data.
      </li>
    </ol>

    <v-alert text border="left" type="warning">
      <ul>
        <li>Imports are currently limited to Microsoft SQL Server only.</li>
        <li>
          Remote access to the server is required before it can be accessed. Contact your cloud or network administrator
          to allow the default port (1433) or any other port to access the database. Please ensure that the selected
          ports are allowed through the firewall.
        </li>
        <li>
          Connections need to be setup before they can be used. Contact your account manager to create these
          connections.
        </li>
      </ul>
    </v-alert>

    <p>
      Importing data from another system takes time&mdash;and depending on network conditions may take a while. All
      long-running processes are handed over to a background worker while you work on other items. Running processes are
      highlighted by a progress indicator
      <v-progress-circular indeterminate size="16" width="2" color="success"></v-progress-circular>
      . When the worker has finished importing your data, you will be taken to the
      <router-link :to="{ path: '/help/files', hash: '#view' }">File view</router-link>
      screen.
    </p>

    <h2 class="text-h4" id="fileList" v-intersect="onIntersect">Browsing files</h2>
    <p>
      A complete list of the files you have uploaded is available from the <code>FILES</code> menu. 
    </p>
    <v-card flat outlined color="grey"><v-img contain eager src="@/assets/images/file_table.png"></v-img></v-card>
    <p>
      File list (in list view) shows both
      <abbr title="Name within the database">virtual</abbr>
      and
      <abbr title="Actual name of the file">physical</abbr>
      names of each file, along with the owner and time since it was last updated. The list also provides insight into
      how many unique rows were identified in the data using the supplied unique identifier.
    </p>
    <p>Files are always sorted by date - latest files (either created or modified) appear on top).</p>
    <p>
      Clicking on an item in the file list will load that file in the
      <router-link :to="{ path: '/help/files', hash: '#view' }">File view</router-link>
      page.
    </p>

    <h2 class="text-h4" id="fileShow" v-intersect="onIntersect">Viewing files</h2>
    <p>
      File view mode provides an in-depth look at available data and its properties in different tabs.
    </p>

    <h3 class="text-h5" id="data" v-intersect="onIntersect">Data</h3>
    <p>
      Data tab shows the actual contents of a file. It fetches as many rows as can fit in the screen, and helps identify
      a file from its contents.
    </p>
    <v-card flat outlined color="grey">
      <v-img contain eager src="@/assets/images/file_view_data.png"></v-img>
    </v-card>
    <p>
      The table does not wrap cell contents and instead allows you to scroll to the right if there are more columns than
      can fit on the screen.
    </p>

    <h3 class="text-h5" id="info" v-intersect="onIntersect">Information</h3>
    <p>
      Info tab shows basic statistics of each column in a file. Every statistic provides some insight into the nature of
      underlying data, and can help identify potential problems.
    </p>
    <v-card flat outlined color="grey">
      <v-img contain eager src="@/assets/images/file_view_info.png"></v-img>
    </v-card>
    <p>Click on the <v-icon>mdi-alert-circle-outline</v-icon> icon to reveal statistical charts in a dialog box.</p>
    <v-card flat outlined color="grey">
      <v-img contain eager width="600" class="mx-auto" src="@/assets/images/file_info_statistics.png"></v-img>
    </v-card>

    <h3 class="text-h5" id="details" v-intersect="onIntersect">Details</h3>
    <p>
      Details tab outlines object-level properties, including name of the source file, its owner, and when it was
      created and/or last updated.
    </p>
    <v-card flat outlined color="grey">
      <v-img contain eager src="@/assets/images/file_view_details.png"></v-img>
    </v-card>
    <p></p>
    <v-alert border="left" text type="info">
      Users with elevated privileges only can download a file. Please contact account manager for required permissions.
    </v-alert>

    <h2 class="text-h4" id="fileEdit" v-intersect="onIntersect">Editing files</h2>
    <p>
      Files can be edited by clicking the
      <v-icon>mdi-pencil-outline</v-icon>
      icon in the toolbar when
      <router-link :to="{ path: '/help/files', hash: '#view' }">viewing</router-link>
      a file. Users can performing the following functions.
    </p>

    <h3 class="text-h5" id="compute" v-intersect="onIntersect">Computing dates</h3>
    <p>Compute a date column from Year and Month columns</p>
    <v-card flat outlined color="grey"><v-img contain eager src="@/assets/images/file_edit_compute.png"></v-img></v-card>
    <ol>
      <li>Select the columns that contain YEAR data; e.g. 2010</li>
      <li>Select the columns that contain MONTH data; e.g. 3</li>
      <li>
        Click
        <code class="green white--text">COMPUTE</code>
        button to create a new date column
      </li>
    </ol>

    <h3 class="text-h5" id="rename" v-intersect="onIntersect">Renaming columns</h3>
    <p>Change the title of a data column</p>
    <v-card flat outlined color="grey"><v-img contain eager src="@/assets/images/file_edit_rename.png"></v-img></v-card>
    <ol>
      <li>
        Select a column from the available list under
        <code>OLD NAME</code>
      </li>
      <li>Enter the new name of the selected column</li>
      <li>
        Click
        <code class="green white--text">RENAME</code>
        button to rename the column
      </li>
    </ol>

    <h3 class="text-h5" id="drop" v-intersect="onIntersect">Dropping columns</h3>
    <p>Remove a column from the data frame</p>
    <v-card flat outlined color="grey"><v-img contain eager src="@/assets/images/file_edit_drop.png"></v-img></v-card>
    <ol>
      <li>Select one or more columns that you want to remove</li>
      <li>
        Click
        <code class="red white--text">DROP</code>
        button to remove the selected column(s)
      </li>
    </ol>

    <h3 class="text-h5" id="clean" v-intersect="onIntersect">Data cleaning</h3>
    <p>Clean data by removing junk values and outliers</p>
    <v-card flat outlined color="grey"><v-img contain eager src="@/assets/images/file_edit_clean.png"></v-img></v-card>
    <ol>
      <li>Select one or more columns that you want cleaned</li>
      <li>
        Choose how you want to deal with problems in the data
        <ul>
          <li>Whether you want to completely remove columns with no data</li>
          <li>Remove columns that are entirely empty</li>
          <li>Replace null values with zeros</li>
        </ul>
      </li>
      <li>
        Click
        <code class="blue white--text">CLEAN</code>
        button to clean the selected column(s)
      </li>
    </ol>

    <h3 class="text-h5" id="holdback" v-intersect="onIntersect">Holdback</h3>
    <p>Remove some months of production data to create a new file for use in <router-link :to="{ path: '/help/applications/holdback' }">Holdback Analysis</router-link>.</p>
    <v-card flat outlined color="grey"><v-img contain eager src="@/assets/images/file_edit_holdback.png"></v-img></v-card>

    <h2 class="text-h4" id="fileDelete" v-intersect="onIntersect">Deleting files</h2>
    <p>
      When viewing a file, click
      <v-icon>mdi-trash-can-outline</v-icon>
      to delete a file. The delete page shows a warning and requires confirmation from the user before deleting a file.
      Click
      <code class="red white--text">DELETE</code>
      to permanently delete the file.
    </p>
    <v-card flat outlined color="grey"><v-img contain eager src="@/assets/images/file_delete.png"></v-img></v-card>
    <v-alert border="left" text type="error">
      <ul>
        <li>
          Deleting a file will remove
          <strong>ALL</strong>
          related datasets and predictions
        </li>
        <li>Deleted files cannot be recovered</li>
      </ul>
    </v-alert>

    <contents :items="menu" ref="contents" />
  </article>
</template>
<script>
import Contents from "@/components/contents"
export default {
  name: "HelpFiles",
  components: { Contents },
  data() {
    return {
      menu: [
        { id: "intro", text: "Introduction", depth: 1 },
        { id: "fileCreate", text: "Creating files", depth: 1 },
        { id: "upload", text: "Uploading", depth: 2 },
        { id: "allowed", text: "Allowed types", depth: 3 },
        { id: "limits", text: "Size limits", depth: 3 },
        { id: "import", text: "Importing", depth: 2 },
        { id: "fileList", text: "Browsing files", depth: 1 },
        { id: "fileShow", text: "Viewing files", depth: 1 },
        { id: "data", text: "Data", depth: 2 },
        { id: "info", text: "Information", depth: 2 },
        { id: "details", text: "Details", depth: 2 },
        { id: "fileEdit", text: "Editing files", depth: 1 },
        { id: "compute", text: "Compute columns", depth: 2 },
        { id: "rename", text: "Rename columns", depth: 2 },
        { id: "drop", text: "Drop columns", depth: 2 },
        { id: "clean", text: "Clean data", depth: 2 },
        { id: "holdback", text: "Holdback data", depth: 2 },
        { id: "fileDelete", text: "Deleting files", depth: 1 }
      ],
      completion: {
        headers: [
          { text: "Well API", value: "api" },
          { text: "County", value: "county" },
          { text: "Surface Latitude", value: "latitude" },
          { text: "Surface Longitude", value: "longitude" },
          { text: "Lateral Length", value: "length" },
          { text: "Completion Date", value: "date" },
          { text: "Proppant Amount", value: "proppant" }
        ],
        items: [
          {
            api: "1000000000",
            county: "County A",
            latitude: "30.000000",
            longitude: "101.000000",
            length: 4500,
            date: "2010-10-01",
            proppant: 300
          },
          {
            api: "1000000001",
            county: "County B",
            latitude: "30.500000",
            longitude: "101.500000",
            length: 4600,
            date: "2010-11-01",
            proppant: 250
          },
          {
            api: "1000000002",
            county: "County C",
            latitude: "32.000000",
            longitude: "102.000000",
            length: 5000,
            date: "2010-12-01",
            proppant: 150
          },
          {
            api: "1000000003",
            county: "County A",
            latitude: "32.000000",
            longitude: "102.000000",
            length: 5432,
            date: "2010-10-01",
            proppant: 350
          },
          {
            api: "1000000004",
            county: "County B",
            latitude: "32.550000",
            longitude: "102.550000",
            length: 3500,
            date: "2010-11-01",
            proppant: 450
          }
        ]
      },
      production: {
        headers: [
          { text: "Well API", value: "api" },
          { text: "Production Date", value: "date" },
          { text: "Oil Volume", value: "oil" },
          { text: "Gas Volume", value: "gas" },
          { text: "Water Volume", value: "water" }
        ],
        items: [
          {
            api: "1000000000",
            date: "2010-06-01",
            oil: 500,
            gas: 300,
            water: 5000
          },
          {
            api: "1000000000",
            date: "2010-07-01",
            oil: 1500,
            gas: 400,
            water: 4000
          },
          {
            api: "1000000000",
            date: "2010-08-01",
            oil: 10000,
            gas: 500,
            water: 3000
          },
          {
            api: "1000000000",
            date: "2010-09-01",
            oil: 15000,
            gas: 250,
            water: 2000
          },
          {
            api: "1000000000",
            date: "2010-10-01",
            oil: 12000,
            gas: 100,
            water: 1000
          }
        ]
      }
    }
  }
}
</script>
